import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Star, BookOpen, Compass, Settings2, Check } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Drawer, DrawerContent, DrawerTitle } from "./ui/drawer";

export const STATUS_LABEL = { planning: "Planning", current: "Current", completed: "Completed", past: "Past" };

export const fmtRange = (a, b) => {
  const f = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
  return [f(a), f(b)].filter(Boolean).join(" – ") || "Dates TBD";
};

// Header pill: which journey am I looking at? Tap → sheet listing current + past (+ planning for admins).
export default function TripSwitcher({ tone = "dark" }) {
  const { user, org, config, setViewTrip, isLookingBack } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const isAdmin = user?.role === "admin";
  const light = tone === "light";

  useEffect(() => {
    if (!open) return;
    api.get("/trips").then((r) => setData(r.data)).catch(() => setData({ trips: [] }));
  }, [open]);

  if (!config?.trip_name) return null;
  const trips = data?.trips || [];
  const currentId = org?.current_trip_id;
  const live = trips.filter((t) => t.status === "current" || t.status === "completed");
  const planning = trips.filter((t) => t.status === "planning");
  const past = trips.filter((t) => t.status === "past");

  return (
    <>
      <button
        type="button"
        data-testid="trip-switcher-pill"
        onClick={() => setOpen(true)}
        className={`mt-0.5 inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full pl-2.5 pr-2 py-0.5 text-[11px] font-semibold transition-colors ${
          isLookingBack
            ? "bg-[#E0A73A]/20 text-[#B87309] border border-[#E0A73A]/50"
            : light ? "bg-white/10 text-white/90 border border-white/15 hover:bg-white/15" : "bg-[#0d2340]/5 text-[#0d2340] border border-[#0d2340]/10 hover:bg-[#0d2340]/10"
        }`}
        aria-label="Switch journey"
      >
        {isLookingBack ? <BookOpen className="h-3 w-3 shrink-0" /> : null}
        <span className="truncate">{config.trip_name}</span>
        {config.status && config.status !== "current" && <span className="opacity-70">· {STATUS_LABEL[config.status]}</span>}
        <ChevronDown className="h-3 w-3 shrink-0" />
      </button>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="rounded-t-[28px] px-5 pb-8 max-h-[85vh] overflow-y-auto" data-testid="trip-switcher-sheet">
          <DrawerTitle className="sr-only">Switch journey</DrawerTitle>
          <div className="mx-auto w-full max-w-md">
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mt-3">Current journey</div>
            {live.map((t) => <Row key={t.id} t={t} active={t.id === config.id} current onPick={() => setViewTrip(t.id, currentId)} />)}
            {!data && <div className="text-sm text-muted-foreground py-3">Loading…</div>}
            {isAdmin && planning.length > 0 && (
              <>
                <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mt-5">Planning <span className="normal-case tracking-normal font-normal">· hidden from partners</span></div>
                {planning.map((t) => <Row key={t.id} t={t} active={t.id === config.id} onPick={() => setViewTrip(t.id, currentId)} />)}
              </>
            )}
            {past.length > 0 && (
              <>
                <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mt-5 flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Look back</div>
                {past.map((t) => <Row key={t.id} t={t} active={t.id === config.id} onPick={() => setViewTrip(t.id, currentId)} />)}
                <p className="text-xs text-muted-foreground mt-3">Past journeys are read-only. Revisit every dispatch, prayer and photo — just no new posts or comments.</p>
              </>
            )}
            {isAdmin && (
              <button data-testid="trip-switcher-manage" onClick={() => { setOpen(false); navigate("/app/trips"); }}
                className="mt-5 w-full flex items-center justify-center gap-2 rounded-full border border-[#0d2340]/15 text-[#0d2340] py-3 text-sm font-semibold hover:bg-[#0d2340]/5">
                <Settings2 className="h-4 w-4" /> Manage journeys
              </button>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

function Row({ t, active, current, onPick }) {
  const s = t.stats || {};
  return (
    <button type="button" data-testid={`trip-switcher-row-${t.id}`} onClick={onPick}
      className={`mt-2 w-full text-left rounded-2xl p-3.5 flex items-center gap-3 transition-colors ${active ? "border-2 border-[#C9973A]/60 bg-[#C9973A]/5" : "bg-[#0d2340]/[0.04] hover:bg-[#0d2340]/[0.08]"}`}>
      <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 text-white" style={{ background: current ? "linear-gradient(135deg,#0d2340,#2b5a8f)" : "#8a8a99" }}>
        {current ? <Compass className="h-5 w-5" /> : <BookOpen className="h-5 w-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-[#0d2340] truncate">{t.trip_name}</div>
        <div className="text-xs text-muted-foreground truncate">{fmtRange(t.start_date, t.end_date)}{s.dispatches != null ? ` · ${s.dispatches} dispatches · ${s.prayers} prayers` : ""}</div>
      </div>
      {active ? <Check className="h-4 w-4 text-[#C9973A]" /> : current ? <Star className="h-4 w-4 text-[#C9973A]" /> : <span className="text-muted-foreground">→</span>}
    </button>
  );
}
